import { Injectable } from '@angular/core';
import { notificationError } from '@components/layout/error/error.utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@store/state';
import { ThresholdService } from '@service/threshold.service';
import { getEmailEventTypesByProductIdMap } from '@store/products/products.selector';
import { changeThresholdsFilters, 
  changeThresholdsFiltersSuccess, 
  changeThresholdsFiltersFailure,
  loadThresholds,
  loadThresholdsSuccess,
  loadThresholdsFailure,
  upsertNewThresholds,
  upsertNewThresholdsSuccess,
  upsertNewThresholdsFailure,
  deleteThresholds,
  deleteThresholdsSuccess,
  deleteThresholdsFailure,
  updateThresholdPriority,
  updateThresholdPrioritySuccess,
  updateThresholdPriorityFailure
} from '@store/thresholds-list/thresholds-list.action';
import { catchError, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';

@Injectable()
export class ThresholdsListEffect {
  constructor(
    public store: Store<State>,
    public actions$: Actions,
    public thresholdService: ThresholdService
  ) { }

  loadThresholds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadThresholds),
      switchMap(({ productIds }) =>
        this.thresholdService.getThresholds(productIds).pipe(
          map((thresholds) =>
            loadThresholdsSuccess({
              thresholds,
            })
          ),
          catchError((error) =>
            of(
              loadThresholdsFailure({
                error: notificationError(error),
              })
            )
          )
        )
      )
    )
  );

  updateProductId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeThresholdsFilters),
      withLatestFrom(this.store.select(getEmailEventTypesByProductIdMap)),
      map(([{ filters }, emailEventTypesByProductId]) => {
        if (filters.productId.length === 0) {
          return changeThresholdsFiltersSuccess({
            filters: { ...filters, eventType: [] },
          });
        }
        const validEventTypes = [
          ...new Set(filters.productId.flatMap((productId) => emailEventTypesByProductId[productId] ?? []))
        ];
        return changeThresholdsFiltersSuccess({
          filters: {
            ...filters,
            eventType: filters.eventType.filter((et) => validEventTypes.includes(et)),
          },
        });
      })
    )
  );

  upsertThresholds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(upsertNewThresholds),
      switchMap(({ newThreshold }) =>
        this.thresholdService.upsertThreshold(newThreshold).pipe(
          mergeMap(() => [
            upsertNewThresholdsSuccess({
              newThreshold,
            }),
            loadThresholds({ productIds: [newThreshold.productId] }) // Reload thresholds from backend to get updated lastModifiedBy
          ]),
          catchError((error) =>
            of(
              upsertNewThresholdsFailure({
                error: notificationError(error),
                newThreshold,
              })
            )
          )
        )
      )
    )
  );

  deleteThresholds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteThresholds),
      switchMap(({ productId, eventType, indicatorName, triggerType }) =>
        this.thresholdService.deleteThreshold(productId, eventType, indicatorName, triggerType).pipe(
          map(() =>
            deleteThresholdsSuccess({
              productId,
              eventType,
              indicatorName,
              triggerType,
            })
          ),
          catchError((error) =>
            of(
              deleteThresholdsFailure({
                error: notificationError(error),
                productId,
                eventType,
                indicatorName,
                triggerType,
              })
            )
          )
        )
      )
    )
  );

  updateThresholdPriority$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateThresholdPriority),
      switchMap(({ productId, eventType, priority }) =>
        this.thresholdService.updatePriority(productId, eventType, priority).pipe(
          map(() =>
            updateThresholdPrioritySuccess({
              productId,
              eventType,
              priority,
            })
          ),
          catchError((error) =>
            of(
              updateThresholdPriorityFailure({
                error: notificationError(error),
                productId,
                eventType,
                priority,
              })
            )
          )
        )
      )
    )
  );
}
